import { useState, type FormEvent } from 'react';
import { motion } from 'framer-motion';
import {
  ArrowRightLeft,
  ArrowRight,
  Plus,
  RotateCcw,
  Calendar,
  Landmark,
  Wallet,
  CreditCard,
  CheckCircle2,
  IndianRupee,
  Layers,
  FileText
} from 'lucide-react';
import { formatMoney } from '@vns/core';
import {
  useFundTransfers,
  useCreateFundTransfer,
  useBankAccounts,
  type NewFundTransfer,
} from '../lib/finance';
import { PAYMENT_MODES, todayLocal } from '../lib/lookups';
import Shell from '../components/Shell';
import {
  Drawer,
  Field,
  EmptyState,
  TableSkeleton,
  ErrorState,
  formatDate,
  useToast,
  Select,
} from '../components/ui';

export default function FundTransfers() {
  const [adding, setAdding] = useState(false);
  const { data, isLoading, error, refetch } = useFundTransfers();

  // KPI Metrics
  const totalCount = data?.length ?? 0;
  const totalVolumePaise = data?.reduce((s, t) => s + (t.amountPaise ?? 0), 0) ?? 0;

  return (
    <Shell
      title="Inter-Account Fund Transfers"
      crumb="Finance"
      actions={
        <button
          className="btn btn-primary btn-auto btn-sm"
          onClick={() => setAdding(true)}
          style={{ display: 'flex', alignItems: 'center', gap: 6, borderRadius: 'var(--radius-sm)' }}
        >
          <Plus size={15} strokeWidth={2.2} />
          <span>New Fund Transfer</span>
        </button>
      }
    >
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
      >
        <p style={{ marginTop: 0, marginBottom: 18, color: 'var(--text-soft)', fontSize: 13.5 }}>
          Money moved between internal company accounts — bank-to-bank, or bank withdrawals for site petty cash floats.
          Each transaction automatically updates the debit and credit balances simultaneously.
        </p>

        {/* Financial KPI Row */}
        {!isLoading && !!data?.length && (
          <div className="kpi-row" style={{ marginBottom: 20 }}>
            <div className="kpi">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                <span className="lbl">Total Transfers</span>
                <div style={{ color: 'var(--brand)', background: 'rgba(37, 99, 235, 0.1)', padding: 5, borderRadius: 'var(--radius-xs)' }}>
                  <ArrowRightLeft size={14} />
                </div>
              </div>
              <div className="val num" style={{ fontSize: 20 }}>{totalCount}</div>
              <div style={{ fontSize: 11.5, color: 'var(--text-faint)', marginTop: 4 }}>
                Internal fund movements logged
              </div>
            </div>

            <div className="kpi">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                <span className="lbl">Total Volume Transferred</span>
                <div style={{ color: '#16a34a', background: 'rgba(22, 163, 74, 0.1)', padding: 5, borderRadius: 'var(--radius-xs)' }}>
                  <IndianRupee size={14} />
                </div>
              </div>
              <div className="val num" style={{ fontSize: 20, color: '#16a34a' }}>
                {formatMoney(totalVolumePaise)}
              </div>
              <div style={{ fontSize: 11.5, color: 'var(--text-faint)', marginTop: 4 }}>
                Cumulative capital relocated
              </div>
            </div>
          </div>
        )}

        {/* Center-Aligned Transfers Table */}
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div className="table-wrap">
            {isLoading ? (
              <TableSkeleton rows={4} cols={7} />
            ) : error ? (
              <ErrorState error={error} onRetry={() => void refetch()} />
            ) : !data?.length ? (
              <EmptyState
                icon="transfer"
                title="No fund transfers recorded yet"
                hint="Record internal capital transfers between bank accounts or petty cash top-ups."
              />
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Source Account</th>
                    <th>Destination Account</th>
                    <th>Amount</th>
                    <th>Transfer Mode</th>
                    <th>UTR / Ref Number</th>
                    <th>Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((t) => (
                    <tr key={t.id}>
                      <td>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: 'var(--text-soft)' }}>
                          <Calendar size={12} style={{ color: 'var(--text-faint)' }} />
                          <span>{formatDate(t.date)}</span>
                        </span>
                      </td>

                      <td>
                        <span style={{ fontWeight: 500, color: 'var(--text)' }}>
                          {t.fromName}
                        </span>
                      </td>

                      <td>
                        <span
                          style={{
                            fontWeight: 600,
                            color: 'var(--brand)',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                          }}
                        >
                          <ArrowRight size={12} />
                          <span>{t.toName}</span>
                        </span>
                      </td>

                      <td className="num">
                        <span style={{ fontWeight: 700, fontSize: 13.5, color: 'var(--text)' }}>
                          {formatMoney(t.amountPaise)}
                        </span>
                      </td>

                      <td>
                        <span
                          style={{
                            textTransform: 'uppercase',
                            fontSize: 11,
                            fontWeight: 600,
                            padding: '2px 8px',
                            borderRadius: 'var(--radius-xs)',
                            background: 'var(--surface-muted)',
                            border: '1px solid var(--border)',
                            color: 'var(--text-soft)',
                          }}
                        >
                          {t.mode}
                        </span>
                      </td>

                      <td className="num">
                        {t.utrNo || t.referenceNo ? (
                          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-soft)' }}>
                            {t.utrNo || t.referenceNo}
                          </span>
                        ) : (
                          <span style={{ color: 'var(--text-faint)' }}>—</span>
                        )}
                      </td>

                      <td>
                        <span style={{ color: 'var(--text-soft)', fontSize: 12.5 }}>
                          {t.remarks ?? '—'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ background: 'var(--surface-muted)' }}>
                    <td colSpan={3} style={{ fontWeight: 700, textAlign: 'right', paddingRight: 14 }}>
                      Total Transferred:
                    </td>
                    <td className="num" style={{ fontWeight: 800, fontSize: 13.5, color: '#16a34a' }}>
                      {formatMoney(totalVolumePaise)}
                    </td>
                    <td colSpan={3} />
                  </tr>
                </tfoot>
              </table>
            )}
          </div>
        </div>
      </motion.div>

      {adding && <AddTransferDrawer onClose={() => setAdding(false)} />}
    </Shell>
  );
}

const EMPTY: NewFundTransfer = {
  from_account_id: '',
  to_account_id: '',
  date: todayLocal(),
  amountRupees: '',
  mode: 'neft',
  reference_no: '',
  utr_no: '',
  remarks: '',
};

function AddTransferDrawer({ onClose }: { onClose: () => void }) {
  const [form, setForm] = useState<NewFundTransfer>(EMPTY);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { data: accounts, isLoading } = useBankAccounts();
  const create = useCreateFundTransfer();
  const toast = useToast();

  const set = (k: keyof NewFundTransfer) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const from = accounts?.find((a) => a.id === form.from_account_id);
  const amount = Number((form.amountRupees ?? '').replace(/[₹,\s]/g, ''));

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const next: Record<string, string> = {};
    if (!form.from_account_id) next.from_account_id = 'Choose the originating debit account';
    if (!form.to_account_id) next.to_account_id = 'Choose the receiving credit account';
    else if (form.to_account_id === form.from_account_id) next.to_account_id = 'Source and destination accounts must be different';
    const amt = (form.amountRupees ?? '').replace(/[₹,\s]/g, '');
    if (!amt) next.amountRupees = 'Amount is required';
    else if (!/^\d+(\.\d{1,2})?$/.test(amt) || Number(amt) <= 0) next.amountRupees = 'Enter a valid positive transfer amount';
    setErrors(next);
    if (Object.keys(next).length) return;

    try {
      await create.mutateAsync({ ...form, amountRupees: amt });
      toast.show('Fund transfer recorded successfully');
      onClose();
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'Could not save the transfer', true);
    }
  }

  const noAccounts = !isLoading && (accounts?.length ?? 0) < 2;

  return (
    <Drawer
      title="New Fund Transfer"
      description="Record internal funds movement between company accounts"
      onClose={onClose}
      onSubmit={onSubmit}
      submitLabel="Record Transfer"
      busy={create.isPending}
    >
      {noAccounts && (
        <div
          className="alert"
          style={{
            marginBottom: 16,
            borderRadius: 'var(--radius-sm)',
            border: '1px solid rgba(234, 88, 12, 0.2)',
            background: 'rgba(234, 88, 12, 0.08)',
            color: '#ea580c',
            padding: '10px 14px',
            fontSize: 13,
          }}
        >
          You need at least two registered accounts before you can record transfers between them.
        </div>
      )}

      <div className="form-grid">
        <Field label="Transfer Date" span2>
          <input type="date" value={form.date} onChange={set('date')} />
        </Field>

        <Field label="Source Account (Debit)" error={errors.from_account_id}>
          <Select
            value={form.from_account_id}
            onChange={(val) => set('from_account_id')({ target: { value: val } } as any)}
            disabled={isLoading}
            placeholder={isLoading ? 'Loading accounts…' : 'Choose Source Account'}
            options={[
              { value: '', label: isLoading ? 'Loading accounts…' : 'Choose Source Account' },
              ...(accounts?.map((a) => ({
                value: a.id,
                label: `${a.bankName} (${a.isCash ? 'Cash' : 'Bank'}) — ${formatMoney(a.balancePaise)}`,
              })) ?? []),
            ]}
            className={errors.from_account_id ? 'border-rose-500' : ''}
          />
        </Field>

        <Field label="Destination Account (Credit)" error={errors.to_account_id}>
          <Select
            value={form.to_account_id}
            onChange={(val) => set('to_account_id')({ target: { value: val } } as any)}
            disabled={isLoading}
            placeholder={isLoading ? 'Loading accounts…' : 'Choose Destination Account'}
            options={[
              { value: '', label: isLoading ? 'Loading accounts…' : 'Choose Destination Account' },
              ...(accounts
                ?.filter((a) => a.id !== form.from_account_id)
                .map((a) => ({
                  value: a.id,
                  label: `${a.bankName} (${a.isCash ? 'Cash' : 'Bank'}) — ${formatMoney(a.balancePaise)}`,
                })) ?? []),
            ]}
            className={errors.to_account_id ? 'border-rose-500' : ''}
          />
        </Field>

        <Field
          label="Transfer Amount (₹)"
          error={errors.amountRupees}
          hint={
            !errors.amountRupees && from && amount > 0 && amount * 100 > from.balancePaise
              ? `Warning: Exceeds the ${formatMoney(from.balancePaise)} balance currently showing in ${from.bankName}`
              : undefined
          }
        >
          <input
            value={form.amountRupees}
            onChange={set('amountRupees')}
            inputMode="decimal"
            aria-invalid={!!errors.amountRupees}
          />
        </Field>

        <Field label="Transfer Mode">
          <Select
            value={form.mode}
            onChange={(val) => set('mode')({ target: { value: val } } as any)}
            options={PAYMENT_MODES.map((m) => ({ value: m.value, label: m.label }))}
          />
        </Field>

        <Field label="Reference Number" optional>
          <input
            value={form.reference_no}
            onChange={set('reference_no')}
          />
        </Field>

        <Field label="UTR / Transaction ID" optional>
          <input
            value={form.utr_no}
            onChange={set('utr_no')}
          />
        </Field>

        <Field label="Remarks & Notes" optional span2>
          <input
            value={form.remarks}
            onChange={set('remarks')}
          />
        </Field>
      </div>
    </Drawer>
  );
}
